import React from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { SportsSoccer, Star, EmojiEvents } from '@mui/icons-material';
import colors from '../../design-system/colors';
import spacing from '../../design-system/spacing';
import shadows from '../../design-system/shadows';

/**
 * PlayerCard - Card de jugador con foto, stats clave y badges
 * Para mostrar información resumida de jugadores
 */

const StyledCard = styled(motion.div)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: spacing.borderRadius.lg,
  overflow: 'hidden',
  boxShadow: shadows.card,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,

  '&:hover': {
    boxShadow: shadows.cardHover,
    transform: 'translateY(-4px)',
    borderColor: colors.primary[300],
  },
}));

const HeaderSection = styled(Box)({
  background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 100%)`,
  padding: spacing[3],
  display: 'flex',
  alignItems: 'center',
  gap: spacing[2],
});

const StatItem = ({ icon: Icon, label, value, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        mb: 0.5,
      }}
    >
      {Icon && <Icon sx={{ fontSize: 16, color: color || 'text.secondary' }} />}
      <Typography variant="h6" fontWeight={700} color={color || 'text.primary'}>
        {value}
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const PlayerCard = ({
  nombre,
  dorsal,
  posicion,
  foto,
  goles = 0,
  asistencias = 0,
  partidos = 0,
  minutos = 0,
  valoracion,
  esCapitan = false,
  esMVP = false,
  onClick,
  equipoEscudo,
}) => {
  // Generar iniciales si no hay foto
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <StyledCard
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Header con foto y nombre */}
      <HeaderSection>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={foto}
            sx={{
              width: 64,
              height: 64,
              border: `3px solid ${colors.neutral.white}`,
              fontSize: 24,
              fontWeight: 700,
              backgroundColor: colors.primary[400],
            }}
          >
            {getInitials(nombre)}
          </Avatar>
          {/* Dorsal badge */}
          {dorsal && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: colors.secondary[500],
                border: `2px solid ${colors.neutral.white}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {dorsal}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              color: colors.neutral.white,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nombre}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            {posicion && (
              <Chip
                label={posicion}
                size="small"
                sx={{
                  backgroundColor: colors.neutral.white + '30',
                  color: colors.neutral.white,
                  fontSize: 11,
                  height: 20,
                }}
              />
            )}
            {esCapitan && (
              <Chip
                icon={<Star sx={{ fontSize: 12, color: colors.secondary[500] }} />}
                label="Capitán"
                size="small"
                sx={{
                  backgroundColor: colors.neutral.white,
                  // Fixed dark text: this chip's background is always solid
                  // white regardless of app theme, so its text must stay dark
                  // (not follow theme.palette.text.primary, which turns light
                  // in dark mode and would be invisible here).
                  color: colors.text.primary,
                  fontSize: 11,
                  height: 20,
                }}
              />
            )}
            {esMVP && (
              <Chip
                icon={<EmojiEvents sx={{ fontSize: 12, color: colors.secondary[600] }} />}
                label="MVP"
                size="small"
                sx={{
                  backgroundColor: colors.secondary[100],
                  // Fixed dark text for the same reason as the Capitán chip
                  // above: a fixed light-yellow background needs fixed dark
                  // text, independent of theme mode.
                  color: colors.text.primary,
                  fontSize: 11,
                  height: 20,
                }}
              />
            )}
          </Box>
        </Box>
      </HeaderSection>

      {/* Stats principales */}
      <Box sx={{ p: spacing[3] }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: spacing[2],
          }}
        >
          <StatItem icon={SportsSoccer} label="Goles" value={goles} color={colors.football.win} />
          <StatItem label="Asistencias" value={asistencias} color={colors.primary[600]} />
          <StatItem label="Partidos" value={partidos} />
          <StatItem label="Minutos" value={minutos.toLocaleString()} />
        </Box>

        {/* Valoración si existe */}
        {valoracion && (
          <Box
            sx={{
              mt: spacing[2],
              pt: spacing[2],
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              Valoración media
            </Typography>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: valoracion >= 7 ? colors.football.win : valoracion >= 5 ? colors.football.draw : colors.football.loss,
              }}
            >
              {valoracion.toFixed(1)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Escudo del equipo (opcional) */}
      {equipoEscudo && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            opacity: 0.2,
          }}
        >
          <img src={equipoEscudo} alt="Escudo" width={32} height={32} />
        </Box>
      )}
    </StyledCard>
  );
};

export default PlayerCard;
